import { FC, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableTextCellProps {
  text: string | null | undefined;
  /** Lines shown when collapsed. Default: 2 */
  collapsedLines?: number;
  className?: string;
}

/**
 * Renders a long text string with a line-clamp by default and a chevron
 * toggle that appears when the text overflows. Mirrors the UX of
 * ExpandableTagsCell but for prose / freeform strings (e.g. transfer
 * reasons, notes, summaries).
 */
export const ExpandableTextCell: FC<ExpandableTextCellProps> = ({
  text,
  collapsedLines = 2,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      // scrollHeight reflects the full unclamped height; clientHeight the
      // clamped one. If they differ, content is being truncated.
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, collapsedLines]);

  if (!text) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="flex items-start gap-1.5">
      <p
        ref={ref}
        className={className}
        style={
          expanded
            ? undefined
            : {
                display: '-webkit-box',
                WebkitLineClamp: collapsedLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
        }
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
          className="shrink-0 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title={expanded ? 'Collapse' : 'Show full text'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
};

export default ExpandableTextCell;
